import type { BlockCardsImage } from 'types/block-cards-image';
import React from 'react';
import Image from 'next/image';
import { Wrap, Paragraph, Button } from '@leafwell/components';
import HeadingBody from './heading-body';
import Figure from './figure';
import WysiwygContent from './wysiwyg-content';

const CardsImage: React.FC<BlockCardsImage> = ({
  sectionId,
  title,
  label,
  labelPosition,
  description,
  button,
  listNumbered = 'true',
  cards,
}) => {
  const ListWrapper = listNumbered ? 'ol' : 'ul';
  return (
    <Wrap id={sectionId} className="justify-items-center gap-y-12">
      {title?.length > 0 || label?.length > 0 ? (
        <div className="grid gap-y-4 text-center">
          {title?.length > 0 && (
            <HeadingBody className="font-semibold" {...{ level: '2', title }} />
          )}
          {label?.length > 0 ? (
            <p
              className="text-lg lg:text-xl"
              dangerouslySetInnerHTML={{ __html: label }}
            />
          ) : null}
        </div>
      ) : null}
      {cards?.length > 0 ? (
        <ListWrapper className="flex flex-wrap items-start justify-start md:justify-center gap-x-10 md:gap-y-12">
          {cards?.map(({ title, description, image }, idx) => {
            return (
              <li
                key={`${sectionId}-${idx}`}
                className="flex md:flex-col items-start md:items-center justify-center gap-4"
              >
                {image && (
                  <Image
                    src={image.src}
                    alt=""
                    height={70}
                    width={70}
                    className=""
                  />
                )}
                <div className="flex flex-col gap-y-2 md:w-72">
                  {title?.length > 0 && (
                    <HeadingBody
                      tagName="p"
                      level="6"
                      title={listNumbered ? `${idx + 1}. ${title}` : `${title}`}
                      className="!text-lg font-semibold text-left md:text-center"
                    />
                  )}
                  {description?.length > 0 && (
                    <Paragraph
                      tagName="div"
                      className="text-left md:text-center !text-base"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                  <span className="md:hidden my-6 w-full flex justify-start">
                    <hr className="w-3/4 border-t-2 border-gray-300" />
                  </span>
                </div>
              </li>
            );
          })}
        </ListWrapper>
      ) : null}
      {description?.length > 0 ? (
        <div className="mt-10 grid gap-y-6 justify-items-center">
          <WysiwygContent content={description} className="max-w-96" />
          {button && (
            <Button
              href={button.url}
              label={button.title}
              rounded={true}
              variant="secondary"
              filled={false}
              target={button.target}
              className="w-fit h-fit"
            />
          )}
        </div>
      ) : null}
    </Wrap>
  );
};

export default CardsImage;
